import {
  NUMBER_KINGDOM_DISTRICTS,
  completeNumberKingdomDistrict,
  completeNumberForgeProgress,
  completeRegroupingWorkshopProgress,
  getNumberKingdomState,
  markAdditionCitadelDiscovered,
  markNumberForgeDiscovered,
  markRegroupingWorkshopDiscovered,
  saveRegroupingWorkshopSnapshot,
  startRegroupingWorkshopProgress,
} from '../../NumberKingdomState.js';
import { SaveManager } from '../../../../../state/SaveManager.js';
import type { NumberForgeConfig } from './minigames/NumberForgeConfig.js';
import type { RegroupingWorkshopConfig } from './minigames/RegroupingWorkshopConfig.js';
import {
  RegroupingWorkshopState,
  type RegroupingWorkshopSerializedState,
} from './minigames/RegroupingWorkshopState.js';

export interface AdditionCitadelStateSnapshot {
  readonly discovered: boolean;
  readonly unlocked: boolean;
  readonly completed: boolean;
  readonly currentSubArea: 'twin-towers' | 'number-forge' | 'regrouping-workshop';
  readonly numberForgeDiscovered: boolean;
  readonly numberForgeChallengesCompleted: readonly string[];
  readonly forgePowered: boolean;
  readonly regroupingWorkshopEligible: boolean;
  readonly regroupingWorkshopDiscovered: boolean;
  readonly regroupingWorkshopChallengesStarted: readonly string[];
  readonly regroupingWorkshopChallengesCompleted: readonly string[];
  readonly regroupingWorkshopPowered: boolean;
  readonly regroupingWorkshopSnapshots: Readonly<Record<string, unknown>>;
}

export function getAdditionCitadelState(): AdditionCitadelStateSnapshot {
  const world = getNumberKingdomState();
  return {
    discovered: world.additionCitadelDiscovered,
    unlocked: world.districtUnlocks.includes(NUMBER_KINGDOM_DISTRICTS.additionCitadel),
    completed: world.districtCompletion[NUMBER_KINGDOM_DISTRICTS.additionCitadel] === true,
    currentSubArea: world.additionCitadelSubArea,
    numberForgeDiscovered: world.numberForgeDiscovered,
    numberForgeChallengesCompleted: world.numberForgeChallengesCompleted,
    forgePowered: world.additionCitadelMechanismsPowered.length > 0,
    regroupingWorkshopEligible: world.regroupingWorkshopEligible,
    regroupingWorkshopDiscovered: world.regroupingWorkshopDiscovered,
    regroupingWorkshopChallengesStarted: world.regroupingWorkshopChallengesStarted,
    regroupingWorkshopChallengesCompleted: world.regroupingWorkshopChallengesCompleted,
    regroupingWorkshopPowered: world.regroupingWorkshopPowered,
    regroupingWorkshopSnapshots: world.regroupingWorkshopSnapshots,
  };
}

export function discoverAdditionCitadel() {
  markAdditionCitadelDiscovered();
  return getAdditionCitadelState();
}

export function completeAdditionCitadel() {
  completeNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.additionCitadel);
  return getAdditionCitadelState();
}

/** Unlocks Challenge 2 only after Twin Towers has powered the Citadel gate. */
export function discoverNumberForge() {
  if (!getAdditionCitadelState().completed) return getAdditionCitadelState();
  markNumberForgeDiscovered();
  return getAdditionCitadelState();
}

/**
 * Canonical, idempotent completion adapter for the future production-art view.
 * Mastery is recorded once, on the first persisted completion only.
 */
export function completeNumberForge(config: NumberForgeConfig) {
  const result = completeNumberForgeProgress(config.id, config.completionWorldEvent);
  if (result.firstCompletion) SaveManager.recordAnswer(config.skillId, true);
  return { firstCompletion: result.firstCompletion, state: getAdditionCitadelState() };
}

/** Eligible only after at least one Number Forge challenge has been completed. */
export function discoverRegroupingWorkshop() {
  if (!getAdditionCitadelState().regroupingWorkshopEligible) return getAdditionCitadelState();
  markRegroupingWorkshopDiscovered();
  return getAdditionCitadelState();
}

export function startRegroupingWorkshop(config: RegroupingWorkshopConfig) {
  const result = startRegroupingWorkshopProgress(config.id);
  return { newlyStarted: result.newlyStarted, state: getAdditionCitadelState() };
}

export function persistRegroupingWorkshopState(
  config: RegroupingWorkshopConfig,
  snapshot: RegroupingWorkshopSerializedState,
) {
  new RegroupingWorkshopState(config, snapshot);
  saveRegroupingWorkshopSnapshot(config.id, cloneRegroupingWorkshopSnapshot(snapshot));
  return getAdditionCitadelState();
}

/** Canonical idempotent completion adapter for the future production-art view. */
export function completeRegroupingWorkshop(
  config: RegroupingWorkshopConfig,
  snapshot: RegroupingWorkshopSerializedState,
) {
  const restored = new RegroupingWorkshopState(config, snapshot);
  if (!restored.snapshot.isComplete) {
    return { firstCompletion: false, state: getAdditionCitadelState() };
  }
  const result = completeRegroupingWorkshopProgress(config.id, config.completionWorldEvent);
  saveRegroupingWorkshopSnapshot(config.id, cloneRegroupingWorkshopSnapshot(snapshot));
  if (result.firstCompletion) SaveManager.recordAnswer(config.skillId, true);
  return { firstCompletion: result.firstCompletion, state: getAdditionCitadelState() };
}

function cloneRegroupingWorkshopSnapshot(
  snapshot: RegroupingWorkshopSerializedState,
): RegroupingWorkshopSerializedState {
  return {
    ...snapshot,
    revealedSources: [...snapshot.revealedSources],
    combinedOneIds: [...snapshot.combinedOneIds],
    selectedOneIds: [...snapshot.selectedOneIds],
    hint: snapshot.hint ? { ...snapshot.hint } : undefined,
  };
}
